import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';

import Auth from '../../api/auth';
import GitlabWikiHelpButton from '../../components/GitlabWikiHelpButton';
import Table from '../../components/Table';
import { userRolesLookup } from '../../helpers/lookups';
import useTableApiCall from '../../hooks/useTableApiCall';

import type { TableRowData } from '@/types/tableTypes';
import type { User } from '@/types/user';

const usersGuideSlug = 'user-guide/users';

const Users = () => {
  const navigate = useNavigate();
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [rows, setRows] = useState<User[]>([]);

  const handleRowsUpdate = useCallback(
    (data: unknown[]) => {
      setRows(data as User[]);
    },
    [setRows]
  );

  const { fetchData, setCurrentPage, ...tableData } = useTableApiCall(Auth.fetchV2Users, {
    setRows: handleRowsUpdate,
    isPaginated: true,
    searchColumns: ['email', 'roles', 'organization'],
    endpointParams: {
      limit: itemsPerPage,
    },
  });

  const fields = useMemo(
    () => [
      { title: 'Email', id: 'email', isAdvancedSearchable: true },
      { title: 'Role', id: 'roles', isAdvancedSearchable: true },
      { title: 'Organization', id: 'organization', isAdvancedSearchable: true },
      { title: 'Activated', id: 'activated' },
    ],
    []
  );

  const transformedRows = useMemo(() => {
    return rows.map(user => ({
      key: user.uuid,
      onRowClick: () => navigate(`/users/${user.uuid}`),
      data: [
        { field: 'email', value: user.email },
        { field: 'roles', value: userRolesLookup[user.role] },
        { field: 'organization', value: user.organization?.enterpriseName },
        { field: 'activated', value: user.activated ? 'True' : 'False' },
      ],
    })) as TableRowData[];
  }, [navigate, rows]);

  const tableError = tableData.error ? tableData.error.message : null;

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  return (
    <Table
      key={'users-table'}
      header='Users'
      fields={fields}
      rows={transformedRows}
      headerActionSlot={{
        content: (
          <GitlabWikiHelpButton
            slug={usersGuideSlug}
            tooltipMessageId='usersGuide'
            tooltipDefaultMessage='Users guide'
            modalTitleMessageId='usersGuide'
            modalTitleDefaultMessage='Users guide'
            buttonAriaLabel='Open users guide'
            buttonSx={{ mt: 0 }}
          />
        ),
      }}
      {...tableData}
      pageSize={itemsPerPage}
      setItemsPerPage={(size: number) => {
        setItemsPerPage(size);
      }}
      error={tableError}
      setFilter={tableData.setFilter as (filter: unknown) => void}
      onPageChange={handlePageChange}
      advancedSearch={true}
    />
  );
};

export default Users;
